"use client"

import styled from "@emotion/styled"
import { useCallback, useId, useLayoutEffect, useRef, useState } from "react"
import { extractProperty, replaceRichTextLinks } from "utils"
import PortContainer from "./PortContainer"
import PortTitle from "./PortTitle"

const PORT_BLUE_IMAGE_URL = "/images/port-blue-static.png"

const StyledSection = styled.section<{ $darkMode?: boolean }>`
  --p-bg: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.lightBackgroundColor};
  --p-text: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.lightTextColor
      : props.theme.colors.port.lightTextColor};
  --p-border-color: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.lightBorderColor};
  --p-border-color-hover: ${(props) => props.theme.colors.faqTextColorHover};
  --p-item-bg: white;
  --p-item-bg-hover: ${(props) => props.theme.colors.port.lightBackgroundColor};

  padding-block: 60px;
  background-color: var(--p-bg);
  color: var(--p-text);
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 64px;
    padding-block: 80px;
  }

  position: relative;
  overflow: hidden;
  &::after {
    content: url(${PORT_BLUE_IMAGE_URL});
    display: none;

    position: absolute;
    z-index: 1;

    top: -169px;
    inset-inline-end: -3px;
  }
`

const FAQContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`

const StyledFAQItem = styled.div<{ $isExpanded: boolean }>`
  background-color: ${(props) =>
    props.$isExpanded ? "var(--p-item-bg-hover)" : "var(--p-item-bg)"};
  border: 1px solid
    ${(props) => (props.$isExpanded ? "var(--p-border-color-hover)" : "var(--p-border-color)")};
  border-radius: 12px;
  overflow: hidden;
  padding: 0 20px;
  transition: height 0.3s ease, background-color 0.3s ease, border-color 0.3s ease;

  &:hover {
    background-color: var(--p-item-bg-hover);
    border-color: var(--p-border-color-hover);
  }
`

const FAQHeader = styled.button<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 24px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  gap: 16px;
  text-align: inherit;
  transition: all 0.2s ease;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px;
  }

  &:focus {
    outline: 2px solid ${(props) => props.theme.colors.propBlue || "#0000FA"};
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.propBlue || "#0000FA"};
    outline-offset: 2px;
  }
`

const FAQQuestion = styled.span<{ $isExpanded: boolean }>`
  flex: 1;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--p-text);
  transition: color 0.2s ease;
`

const FAQIcon = styled.span<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: ${(props) => props.theme.colors.propBlue};
  font-size: 24px;
  font-weight: 400;
  line-height: 1;
  transition: color 0.2s ease;
  user-select: none;

  ${FAQHeader}:hover &,
  ${FAQHeader}:focus & {
    color: ${(props) => props.theme.colors.propBlue};
  }
`

const FAQContent = styled.div<{ $isExpanded: boolean }>`
  --h-expanded: max-height;
  overflow: hidden;
  background-color: var(--p-item-bg-hover);
  height: ${(props) => (props.$isExpanded ? "var(--h-expanded)" : "0")};
  transition: height 0.3s ease;
`

const FAQAnswer = styled.div`
  padding: 30px 16px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--p-text);

  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 800px;
    margin-right: auto;
  }
  > * {
    &::first-child,
    &::last-child {
      margin-block: 0;
    }
  }
`
const StyledDivider = styled.hr`
  color: var(--p-border-color);
  margin: 0;
  border: none;
  border-top: currentColor 1px solid;
`

type FAQItemData = {
  title: string
  description: string
}

type FAQItemProps = FAQItemData & {
  index: number
  isExpanded: boolean
  headerId: string
  contentId: string
  // eslint-disable-next-line no-unused-vars
  onToggle: (index: number) => void
  // eslint-disable-next-line no-unused-vars
  onKeyDown: (event: React.KeyboardEvent, index: number) => void
}

const FAQItem = ({
  title,
  description,
  index,
  isExpanded,
  headerId,
  contentId,
  onToggle,
  onKeyDown,
}: FAQItemProps) => {
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const setScrollHeight = () => {
      if (ref.current) {
        const { scrollHeight } = ref.current
        ref.current.style.setProperty("--h-expanded", `${scrollHeight}px`)
      }
    }
    setScrollHeight()
    window.addEventListener("resize", setScrollHeight)
    return () => {
      window.removeEventListener("resize", setScrollHeight)
    }
  }, [ref])
  return (
    <StyledFAQItem $isExpanded={isExpanded}>
      <FAQHeader
        id={headerId}
        $isExpanded={isExpanded}
        onClick={() => onToggle(index)}
        onKeyDown={(e) => onKeyDown(e, index)}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        role="button"
        tabIndex={0}
      >
        <FAQQuestion className="faq-question" $isExpanded={isExpanded}>
          {title}
        </FAQQuestion>
        <FAQIcon $isExpanded={isExpanded} aria-hidden="true">
          {isExpanded ? "−" : "+"}
        </FAQIcon>
      </FAQHeader>
      <FAQContent
        id={contentId}
        role="region"
        aria-labelledby={headerId}
        $isExpanded={isExpanded}
        aria-hidden={!isExpanded}
        ref={ref}
      >
        <StyledDivider />
        <FAQAnswer
          dangerouslySetInnerHTML={{
            __html: replaceRichTextLinks(description || ""),
          }}
        />
      </FAQContent>
    </StyledFAQItem>
  )
}

type PortFAQsProps = {
  data: any
}

const PortFAQs = ({ data }: PortFAQsProps) => {
  const blackTitle = extractProperty(data, "blackTitle")
  const blueTitle = extractProperty(data, "blueTitle")
  const subtitle = extractProperty(data, "subtitle")
  const darkMode = extractProperty(data, "darkMode") || false
  const aligned = extractProperty(data, "alignToLeftToggle") ? "left" : "center"

  const itemsList = extractProperty(data, "itemsList") || []
  const items: FAQItemData[] = itemsList?.map((item: any) => ({
    title: extractProperty(item, "title"),
    description: extractProperty(item, "description"),
  }))

  // State for accordion - only one item can be open at a time
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  // Generate unique IDs for accessibility
  const baseId = useId()

  const handleToggle = useCallback(
    (index: number) => {
      if (expandedIndex === index) {
        setExpandedIndex(null)
      } else {
        setExpandedIndex(index)
      }
    },
    [expandedIndex],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, index: number) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        handleToggle(index)
      }
    },
    [handleToggle],
  )

  if (!items || items.length === 0) {
    return null
  }

  return (
    <StyledSection id="port-faqs" $darkMode={!!darkMode}>
      <PortContainer paddingInline={20}>
        {(blackTitle || subtitle) && (
          <PortTitle
            normalText={blackTitle}
            highlightedText={blueTitle}
            subtitle={subtitle}
            titleSx={{
              textAlign: { xs: aligned },
              direction: aligned === "center" ? "ltr" : "rtl",
            }}
            subtitleSx={{
              textAlign: { xs: aligned },
              direction: aligned === "center" ? "ltr" : "rtl",
            }}
          />
        )}
      </PortContainer>
      <PortContainer paddingInline={20}>
        <FAQContainer role="region" aria-label="Frequently Asked Questions">
          {items.map((item, index) => {
            const isExpanded = expandedIndex === index
            const headerId = `${baseId}-header-${index}`
            const contentId = `${baseId}-content-${index}`

            return (
              <FAQItem
                key={index}
                title={item.title}
                description={item.description}
                index={index}
                isExpanded={isExpanded}
                headerId={headerId}
                contentId={contentId}
                onToggle={handleToggle}
                onKeyDown={handleKeyDown}
              />
            )
          })}
        </FAQContainer>
      </PortContainer>
    </StyledSection>
  )
}

export default PortFAQs
